// SearchableSelect — combobox-style picker (PRODOPSAI-521).
//
// Drop-in replacement for native <select> when the option list is long and a
// type-ahead filter would help. The trigger is a <button>, the filter is an
// <input role="combobox">, and the result list is a <ul role="listbox"> with
// each row a <button role="option"> — never `<div>` with onClick (S6847).
//
// Pure helpers (filtering, key navigation, flat-list shape) live in
// `./searchable-select-helpers.ts` so SonarCloud rules S7721 + S3776 cannot
// fire on this file.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { inputClass } from '@/lib/form-styles';
import {
  findOptionByValue,
  flattenForRender,
  nextHighlightIndex,
  type SearchableGroup,
  type SearchableOption,
} from './searchable-select-helpers';

export type { SearchableOption, SearchableGroup };

interface SearchableSelectProps {
  /** Flat option list. Ignored if `groups` is supplied. */
  options?: readonly SearchableOption[];
  /** Optional grouped options (preferred over `options` when set). */
  groups?: readonly SearchableGroup[];
  /** Controlled value (option `.value`). Empty string = nothing selected. */
  value: string;
  /** Fired with the picked option's `.value`. */
  onChange: (value: string) => void;
  /** Placeholder shown on the trigger when nothing is picked. */
  placeholder?: string;
  /** Disables the trigger. */
  disabled?: boolean;
  /** Stable id used for ARIA wiring + test selectors. */
  id?: string;
  /** Optional data-testid on the trigger button. */
  testId?: string;
  /** Optional ARIA label on the trigger when a visible <label> is not used. */
  ariaLabel?: string;
  /**
   * Optional bottom row rendered below the result list. Used for the
   * "+ Create new role…" entry in the team-mapping dialogs.
   */
  footer?: React.ReactNode;
}

export function SearchableSelect({
  options,
  groups,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  id,
  testId,
  ariaLabel,
  footer,
}: Readonly<SearchableSelectProps>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState<number>(-1);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const flatOptions = useMemo(() => options ?? [], [options]);
  const selected = useMemo(
    () => findOptionByValue(flatOptions, groups, value),
    [flatOptions, groups, value],
  );

  const rows = useMemo(
    () => flattenForRender(flatOptions, groups, query),
    [flatOptions, groups, query],
  );

  // Close on outside click + Esc.
  useEffect(() => {
    if (open === false) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (target === null) return;
      if (containerRef.current === null) return;
      if (containerRef.current.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [open]);

  // Focus the input the moment the popover opens.
  useEffect(() => {
    if (open === true) {
      inputRef.current?.focus();
      // Default highlight to the first selectable row.
      setHighlight((cur) => (cur === -1 ? nextHighlightIndex(rows, -1, 1) : cur));
    } else {
      setQuery('');
      setHighlight(-1);
    }
    // We intentionally omit `rows` so we don't reset highlight every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When the query changes — OR when the options/groups change while the
  // panel is open (rows.length is a structural proxy for that) — snap
  // highlight to the first selectable row so Enter picks the top result
  // and the index does not point past the now-truncated list. Rabizard #665
  // finding 3 (PR #665): without rows.length here, replacing `options`
  // while open left `highlight` pointing at a stale index.
  useEffect(() => {
    if (open === true) {
      setHighlight(nextHighlightIndex(rows, -1, 1));
    }
  }, [query, rows, open]);

  const handleTriggerClick = useCallback(() => {
    if (disabled === true) return;
    setOpen((cur) => !cur);
  }, [disabled]);

  const handlePick = useCallback(
    (optValue: string) => {
      onChange(optValue);
      setOpen(false);
    },
    [onChange],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((cur) => nextHighlightIndex(rows, cur, 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((cur) => nextHighlightIndex(rows, cur, -1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const row = rows[highlight];
        if (row !== undefined && row.option !== null) {
          handlePick(row.option.value);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    },
    [rows, highlight, handlePick],
  );

  const triggerLabel = selected === null ? placeholder : selected.label;
  const triggerIsPlaceholder = selected === null;

  const listboxId = id === undefined ? undefined : `${id}-listbox`;
  const activeId =
    listboxId === undefined || highlight < 0 || rows[highlight]?.option === null
      ? undefined
      : `${listboxId}-${rows[highlight].option?.value ?? ''}`;

  // Compute the empty-state row (renders only when the query yields nothing,
  // ignoring the footer slot which is always shown).
  const hasOptionRows = rows.some((r) => r.option !== null);

  return (
    <div ref={containerRef} className="relative" data-testid={testId ?? id}>
      <button
        type="button"
        onClick={handleTriggerClick}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        id={id}
        className={cn(
          inputClass,
          'cursor-pointer text-left',
          triggerIsPlaceholder ? 'text-muted-foreground' : '',
        )}
        data-testid={testId === undefined ? undefined : `${testId}-trigger`}
      >
        {triggerLabel}
      </button>

      {open === true && (
        <div
          className={cn(
            'absolute left-0 right-0 top-full z-40 mt-1 rounded-md border border-border',
            'bg-card text-sm shadow-lg',
          )}
          data-testid={testId === undefined ? undefined : `${testId}-panel`}
        >
          <div className="border-b border-border p-2">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Type to filter…"
              role="combobox"
              aria-controls={listboxId}
              aria-expanded={open}
              aria-activedescendant={activeId}
              aria-autocomplete="list"
              className={inputClass}
              data-testid={testId === undefined ? undefined : `${testId}-input`}
            />
          </div>

          <ul
            id={listboxId}
            role="listbox"
            className="max-h-60 overflow-auto py-1"
            data-testid={testId === undefined ? undefined : `${testId}-list`}
          >
            {rows.map((row, idx) => {
              if (row.option === null) {
                return (
                  <li
                    key={`group-${row.groupHeader ?? ''}-${idx}`}
                    className="px-3 py-1 text-xs font-semibold text-muted-foreground"
                    role="presentation"
                  >
                    {row.groupHeader}
                  </li>
                );
              }
              const isActive = idx === highlight;
              const isSelected = row.option.value === value;
              const optionId =
                listboxId === undefined ? undefined : `${listboxId}-${row.option.value}`;
              return (
                <li key={row.option.value} role="presentation">
                  <button
                    type="button"
                    onClick={() => handlePick(row.option?.value ?? '')}
                    role="option"
                    aria-selected={isSelected}
                    id={optionId}
                    className={cn(
                      'flex w-full flex-col items-start px-3 py-2 text-left text-sm',
                      isActive ? 'bg-muted' : 'hover:bg-muted/50',
                      isSelected ? 'font-medium' : '',
                    )}
                    data-testid={
                      testId === undefined ? undefined : `${testId}-option-${row.option.value}`
                    }
                  >
                    <span>{row.option.label}</span>
                    {row.option.sublabel !== undefined &&
                      row.option.sublabel !== null &&
                      row.option.sublabel !== '' && (
                        <span className="text-xs text-muted-foreground">
                          {row.option.sublabel}
                        </span>
                      )}
                  </button>
                </li>
              );
            })}
            {hasOptionRows === false && (
              <li
                className="px-3 py-2 text-sm text-muted-foreground"
                role="presentation"
                data-testid={
                  testId === undefined ? undefined : `${testId}-empty`
                }
              >
                No matches
              </li>
            )}
          </ul>

          {footer !== undefined && footer !== null && (
            <div
              className="border-t border-border p-1"
              data-testid={testId === undefined ? undefined : `${testId}-footer`}
            >
              {footer}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
